import config from '../configWrapper';
import { getLocalStorage } from '../shared/storage';
import { ApiError } from './httpErrors';
import { UnauthorizedError } from './unauthorizedError';

/**
 * Standard HTTP helper for calls to the Limitless backend (native fetch).
 *
 * - Builds URLs from config.serverUrl (or config.uploadUrl via `baseUrl`)
 * - Sends the dashboard JWT: `authorization: JWT <user.jwtToken>`
 * - Parses JSON (empty bodies resolve to null)
 * - Throws ApiError (with status + server message) on non-2xx responses
 * - On 401, calls the registered unauthorized handler ONCE per dead session
 *   (see setUnauthorizedHandler / sessionExpired.js), then throws UnauthorizedError
 */

export { ApiError, UnauthorizedError };

let unauthorizedHandler = null;
// Token of the session we already reported as dead. Parallel requests that 401 with the same
// (dead) token must not trigger repeated redirects. A new login produces a new token, so it re-arms itself.
let handledToken;

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler;
  handledToken = undefined;
}

export function getJwtToken() {
  try {
    const user = JSON.parse(getLocalStorage('user'));
    return (user && user.jwtToken) || null;
  } catch (e) {
    return null;
  }
}

function buildUrl(path, baseUrl, params) {
  const base = /^https?:\/\//i.test(path) ? '' : (baseUrl || config.serverUrl);
  let url = `${base}${path}`;
  if (params) {
    const query = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        query.append(key, value);
      }
    });
    const queryString = query.toString();
    if (queryString) {
      url += `${url.includes('?') ? '&' : '?'}${queryString}`;
    }
  }
  return url;
}

async function parseBody(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
}

function errorMessage(response, data) {
  if (data && typeof data === 'object' && (data.message || data.statusMessage)) {
    return data.message || data.statusMessage;
  }
  return response.statusText || `Request failed with status ${response.status}`;
}

function notifyUnauthorized(tokenUsed) {
  if (!unauthorizedHandler || tokenUsed === handledToken) {
    return;
  }
  handledToken = tokenUsed;
  unauthorizedHandler();
}

/**
 * @param {string} path - Path such as '/dashboard/sales' (or a full http(s) URL)
 * @param {Object} [options]
 * @param {string} [options.method='GET']
 * @param {Object|string|FormData} [options.body] - Plain objects are JSON stringified
 * @param {Object} [options.params] - Query string params (null/undefined values are skipped)
 * @param {Object} [options.headers] - Extra headers (override the defaults)
 * @param {AbortSignal} [options.signal]
 * @param {string} [options.baseUrl=config.serverUrl] - Use config.uploadUrl for upload service calls
 * @param {boolean} [options.auth=true] - Set false for public calls (login, register, forgot password)
 * @returns {Promise<any>} Parsed JSON (null for empty responses)
 */
export async function apiFetch(path, options = {}) {
  const {
    method = 'GET', body, params, headers = {}, signal, baseUrl, auth = true,
  } = options;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const token = auth ? getJwtToken() : null;

  const requestHeaders = { ...(isFormData ? {} : config.jsonHeader), ...headers };
  if (token) {
    requestHeaders.authorization = `JWT ${token}`;
  }

  const requestOptions = { method, headers: requestHeaders, signal };
  if (body !== undefined && body !== null) {
    requestOptions.body = (isFormData || typeof body === 'string') ? body : JSON.stringify(body);
  }

  const response = await fetch(buildUrl(path, baseUrl, params), requestOptions);
  const data = await parseBody(response);

  if (response.ok) {
    return data;
  }

  const message = errorMessage(response, data);
  if (response.status === 401 && auth) {
    notifyUnauthorized(token);
    throw new UnauthorizedError(message, data);
  }
  throw new ApiError(message, response.status, data);
}

export default apiFetch;
